/* eslint-disable no-param-reassign */
import { CreateAnswersInput, UpdateMemberInput } from '@optimus/models';
import axiosRetry from 'axios-retry';
import { axiosInstance, uninterceptedAxiosInstance } from './apiConfig';
import { EnrollmentRequest, filterNullFields } from '../common/models/EnrollmentRequest';
import { OptimusEvent } from '../common/EventTrackingTypes';
import { BusinessTrade, Member } from '../common/models/Member';
import { Logger } from '../config/logging/logger';

const logger = Logger.create('OnboardingApi');

export const getOnboardingForm = async (formId: string | undefined) => {
  const { data } = await axiosInstance.get(`/forms/${formId}`);
  return data;
};

export const getPublicOnboardingForm =
  async (formId: string | undefined) => uninterceptedAxiosInstance.get(`/forms/public/${formId}`, {
    withCredentials: false,
    'axios-retry': {
      retries: 3,
      retryDelay: axiosRetry.exponentialDelay,
      retryCondition: (error) => {
        // Retry on network errors (timeouts) or specific transient status codes
        const status = error.response?.status;
        if (axiosRetry.isNetworkError(error)) {
          return true;
        }
        // Only retry on server errors (5xx) or specific transient client errors
        return !!status && (status >= 500 || status === 408 || status === 429);
      },
    },
  })
    .then((response) => response.data)
    .catch((err) => {
      logger.error('Failed to fetch public onboarding form', err);
      return { questions: null };
    });

export const getMemberInformation =
  async (memberId: string | undefined) => {
    const { data } = await axiosInstance.get(`/members/${memberId}`);
    return data;
  };

export const enrollMember =
  async (
    solutionOrgId: string | undefined,
    request: EnrollmentRequest,
  ) => {
    const { data } = await axiosInstance.post(`/v2/onboarding/${solutionOrgId}/enrollment`, request);
    return data;
  };

export const verifyPaypalEmail =
  async (
    token: string,
  ) => {
    const response = await uninterceptedAxiosInstance.post(`/members/payment-method-verification?token=${token}`);
    return response;
  };

export const sendEventToOptimusBackend = async (
  optimusEvents: OptimusEvent[],
  eventPath: string,
) => {
  try {
    await axiosInstance.post(`/events/${eventPath}`, optimusEvents);
  } catch (err) {
    // TODO: implement a retry policy mechanism
    logger.error(Logger.format(err));
  }
};

export const getCompanyRoles =
  async () => {
    const { data } = await axiosInstance.get('/members/company-roles');
    return data;
  };

export const updateMemberInformation = async (
  request: UpdateMemberInput | Member,
  memberId: string,
) => {
  const filteredRequest = filterNullFields(request);
  const { data } = await axiosInstance.put(`/members/${memberId}`, filteredRequest);
  return data;
};

export const updateBusinessTrade = async (
  businessTrades: BusinessTrade[] | undefined,
  companyId: string,
) => {
  const { data } = await axiosInstance.post(`/companies/${companyId}/business-trades`, businessTrades);
  return data;
};

export const updateBusinessAnswer = async (value: string, answerId: string) => {
  const { data } = await axiosInstance.put(`/answers/${answerId}`, {
    value,
  });
  return data;
};

export const createBusinessAnswer = async (value: CreateAnswersInput) => {
  const { data } = await axiosInstance.post('/answers', {
    ...value,
  });
  return data;
};
